import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Container from '../ui/Container';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { services } from '../../data/services';

/**
 * Service cards. `limit` trims the list for the Home preview;
 * `detailed` adds the deliverables list used on the Services page.
 */
export default function ServicesGrid({ limit, detailed = false, showCta = true }) {
  const items = limit ? services.slice(0, limit) : services;

  return (
    <section className="section bg-surface" id="services">
      <Container>
        <SectionHeading
          eyebrow="What we do"
          title="End-to-end e-waste services for enterprises"
          description="From the first pickup call to the final EPR credit, every step is handled in-house by a licensed recycler — so accountability never changes hands."
        />

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((service, index) => (
            <Reveal key={service.id} delay={index * 0.06}>
              <article
                id={service.id}
                className="group card flex h-full flex-col p-7 transition-all duration-300 ease-smooth hover:-translate-y-1.5 hover:border-brand-200 hover:shadow-lift"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-50 text-brand-700 transition-all duration-300 group-hover:bg-brand-600 group-hover:text-white">
                  <Icon name={service.icon} className="h-6 w-6" />
                </span>

                <h3 className="mt-6 font-display text-lg font-semibold text-ink-900">
                  {service.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-500">{service.summary}</p>

                {detailed && (
                  <ul className="mt-5 space-y-2.5 border-t border-ink-100 pt-5">
                    {service.deliverables.map((deliverable) => (
                      <li key={deliverable} className="flex gap-2.5 text-sm text-ink-600">
                        <Check className="mt-0.5 h-4 w-4 flex-none text-brand-600" strokeWidth={2.5} />
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                )}

                <p className="mt-auto flex items-center gap-2 pt-6 text-xs font-semibold uppercase tracking-wider text-brand-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                  {service.highlight}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        {showCta && (
          <Reveal className="mt-12 flex flex-wrap items-center justify-center gap-4" delay={0.1}>
            {limit && (
              <Button to="/services" variant="outline" size="lg">
                View all services
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            <Link
              to="/request-pickup"
              className="btn bg-brand-600 px-7 py-3.5 text-white shadow-soft hover:bg-brand-700 hover:shadow-lift"
            >
              Request a pickup
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        )}
      </Container>
    </section>
  );
}
